import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

interface PracticeSession {
  id: string;
  studentId: string;
  lessonId: string;
  plannedTotalMinutes: number;
  plannedDurations: Record<string, number>;
  actualDurations: Record<string, number>;
  completed: boolean;
  startAt: any;
  endAt?: any;
  durationSec?: number;
  taskCounts: Record<string, number>;
  createdAt: any;
  sessionType?: string;
  categoryTimers?: Record<string, { isActive: boolean; elapsed: number }>;
}

interface Task {
  id?: string;
  title: string;
  category: 'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble';
  isCustom: boolean;
  createdBy?: 'student' | 'teacher';
  teacherId?: string;
  practiceReminders?: (string | { content: string; type?: string; level?: number })[];
  defaultMinutes?: number;
}

const Analytics: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Analytics data
  const [weeklyPracticeTime, setWeeklyPracticeTime] = useState(0);
  const [yearlyPracticeTime, setYearlyPracticeTime] = useState(0);
  const [dailyGoalReached, setDailyGoalReached] = useState(0);
  const [categoryBreakdown, setCategoryBreakdown] = useState<Record<string, number>>({});
  const [categoryBreakdownDetailed, setCategoryBreakdownDetailed] = useState<Record<string, { guided: number; free: number }>>({});
  const [totalSessions, setTotalSessions] = useState(0);
  const [averageSessionLength, setAverageSessionLength] = useState(0);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    loadAnalyticsData();
  }, [currentUser, navigate]);

  const loadAnalyticsData = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      
      // Load all completed practice sessions
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', currentUser.uid),
        where('completed', '==', true)
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const sessions: PracticeSession[] = [];
      
      sessionsSnapshot.forEach((doc) => {
        const data = doc.data();
        sessions.push({
          id: doc.id,
          studentId: data.studentId,
          lessonId: data.lessonId,
          plannedTotalMinutes: data.plannedTotalMinutes,
          plannedDurations: data.plannedDurations,
          actualDurations: data.actualDurations,
          completed: data.completed,
          startAt: data.startAt,
          endAt: data.endAt,
          durationSec: data.durationSec,
          taskCounts: data.taskCounts,
          createdAt: data.createdAt,
          sessionType: data.sessionType,
          categoryTimers: data.categoryTimers
        });
      });

      // Sort sessions by endAt date (most recent first)
      sessions.sort((a, b) => {
        const aTime = a.endAt?.toDate?.() || new Date(a.endAt || 0);
        const bTime = b.endAt?.toDate?.() || new Date(b.endAt || 0);
        return bTime.getTime() - aTime.getTime();
      });

      // Calculate weekly practice time
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay()); // Go back to Sunday
      startOfWeek.setHours(0, 0, 0, 0);
      
      const weeklySessions = sessions.filter(session => {
        const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt);
        return sessionDate >= startOfWeek;
      });
      
      const weeklyTime = weeklySessions.reduce((total, session) => {
        return total + (session.durationSec || 0);
      }, 0);
      setWeeklyPracticeTime(weeklyTime);

      // Calculate yearly practice time
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const yearlySessions = sessions.filter(session => {
        const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt);
        return sessionDate >= startOfYear;
      });
      
      const yearlyTime = yearlySessions.reduce((total, session) => {
        return total + (session.durationSec || 0);
      }, 0);
      setYearlyPracticeTime(yearlyTime);

      // Calculate daily goal reached count - only count sessions of 10+ minutes
      const goalMinutes = currentUser.practiceSessionDuration || 30;
      const goalReachedCount = sessions.filter(session => {
        const sessionDurationMinutes = (session.durationSec || 0) / 60;
        return sessionDurationMinutes >= 10 && sessionDurationMinutes >= goalMinutes;
      }).length;
      setDailyGoalReached(goalReachedCount);

      // Calculate category breakdown
      const categoryTime: Record<string, number> = {
        'Scales': 0,
        'Exercises': 0,
        'Studies': 0,
        'Repertoire': 0,
        'Ensemble': 0
      };

      // Load custom tasks to get category information
      const customTasksQuery = query(
        collection(db, 'customTasks'),
        where('studentId', '==', currentUser.uid)
      );
      const customTasksSnapshot = await getDocs(customTasksQuery);
      const customTasks: Task[] = [];
      
      customTasksSnapshot.forEach((doc) => {
        const data = doc.data();
        customTasks.push({
          id: doc.id,
          title: data.title,
          category: data.category,
          isCustom: data.isCustom,
          createdBy: data.createdBy,
          teacherId: data.teacherId,
          practiceReminders: data.practiceReminders,
          defaultMinutes: data.defaultMinutes
        });
      });

      // Calculate time spent in each category (separated by session type)
      const categoryBreakdown: Record<string, { guided: number; free: number }> = {
        'Scales': { guided: 0, free: 0 },
        'Exercises': { guided: 0, free: 0 },
        'Studies': { guided: 0, free: 0 },
        'Repertoire': { guided: 0, free: 0 },
        'Ensemble': { guided: 0, free: 0 }
      };

      sessions.forEach(session => {
        console.log('Processing session:', session.id, 'Type:', session.sessionType);
        
        // Handle Free Practice sessions
        if (session.sessionType === 'free_practice' && session.categoryTimers) {
          // Free practice session category timers processed
          Object.entries(session.categoryTimers).forEach(([category, timerData]) => {
            if (timerData.elapsed > 0) {
              const categoryName = category.charAt(0).toUpperCase() + category.slice(1);
              const minutes = Math.round((timerData.elapsed / 60) * 100) / 100;
              console.log(`Adding ${minutes} minutes to ${categoryName} (Free Practice)`);
              if (categoryBreakdown.hasOwnProperty(categoryName)) {
                categoryBreakdown[categoryName].free += minutes;
              }
            }
          });
        } else {
          // Handle regular practice sessions (Guided Practice)
          Object.entries(session.actualDurations).forEach(([taskId, minutes]) => {
            // Determine category based on taskId
            let category = 'Repertoire'; // Default category
            
            if (taskId.startsWith('free_')) {
              // Handle free practice category timers (shouldn't happen in guided sessions)
              const categoryName = taskId.replace('free_', '');
              category = categoryName.charAt(0).toUpperCase() + categoryName.slice(1);
            } else if (taskId.startsWith('custom_')) {
              const customTaskId = taskId.replace('custom_', '');
              const customTask = customTasks.find(task => task.id === customTaskId);
              if (customTask) {
                category = customTask.category;
              }
            } else if (taskId.startsWith('lesson_')) {
              // For lesson tasks, we'll need to get the category from the lesson
              // For now, we'll use a simple mapping based on common patterns
              category = 'Repertoire'; // Default for lesson tasks
            }
            
            if (categoryBreakdown.hasOwnProperty(category)) {
              categoryBreakdown[category].guided += minutes;
            }
          });
        }
      });

      // Convert to the old format for compatibility
      Object.keys(categoryBreakdown).forEach(category => {
        categoryTime[category] = categoryBreakdown[category].guided + categoryBreakdown[category].free;
      });

      console.log('Final category breakdown:', categoryTime);
      console.log('Detailed category breakdown:', categoryBreakdown);
      setCategoryBreakdown(categoryTime);
      setCategoryBreakdownDetailed(categoryBreakdown);

      // Calculate additional stats - only count sessions of 10+ minutes
      const validSessions = sessions.filter(session => {
        const sessionDurationMinutes = (session.durationSec || 0) / 60;
        return sessionDurationMinutes >= 10;
      });
      setTotalSessions(validSessions.length);
      
      if (validSessions.length > 0) {
        const totalTime = validSessions.reduce((total, session) => total + (session.durationSec || 0), 0);
        const averageMinutes = totalTime / 60 / validSessions.length;
        setAverageSessionLength(averageMinutes);
      }

    } catch (error) {
      console.error('Error loading analytics data:', error);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    } else {
      return `${mins}m`;
    }
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      'Scales': 'bg-gradient-to-r from-blue-500 to-cyan-500',
      'Exercises': 'bg-gradient-to-r from-green-500 to-emerald-500',
      'Studies': 'bg-gradient-to-r from-yellow-500 to-orange-500',
      'Repertoire': 'bg-gradient-to-r from-purple-500 to-pink-500',
      'Ensemble': 'bg-gradient-to-r from-pink-500 to-rose-500'
    };
    return colors[category] || 'bg-gradient-to-r from-gray-500 to-gray-600';
  };

  const getCategoryIcon = (category: string): string => {
    const icons: Record<string, string> = {
      'Scales': '🎼',
      'Exercises': '💪',
      'Studies': '📚',
      'Repertoire': '🎵',
      'Ensemble': '👥'
    };
    return icons[category] || '🎶';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="text-4xl mr-3">📊</div>
              <h1 className="text-lg sm:text-3xl font-bold text-white">Practice Analytics</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Overview Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 overflow-hidden shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="p-3 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-xl sm:text-3xl animate-pulse">📅</div>
                  </div>
                  <div className="ml-2 sm:ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-xs sm:text-sm font-medium text-blue-100 truncate">This Week</dt>
                      <dd className="text-lg sm:text-2xl font-bold text-white">{formatTime(weeklyPracticeTime)}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-emerald-500 overflow-hidden shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="p-3 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-xl sm:text-3xl animate-pulse">📆</div>
                  </div>
                  <div className="ml-2 sm:ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-xs sm:text-sm font-medium text-green-100 truncate">This Year</dt>
                      <dd className="text-lg sm:text-2xl font-bold text-white">{formatTime(yearlyPracticeTime)}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-red-500 overflow-hidden shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="p-3 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-xl sm:text-3xl animate-bounce">🎯</div>
                  </div>
                  <div className="ml-2 sm:ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-xs sm:text-sm font-medium text-orange-100 truncate">Goals Reached</dt>
                      <dd className="text-lg sm:text-2xl font-bold text-white">{dailyGoalReached}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-pink-500 overflow-hidden shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="p-3 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-xl sm:text-3xl animate-pulse">📊</div>
                  </div>
                  <div className="ml-2 sm:ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-xs sm:text-sm font-medium text-purple-100 truncate">Total Sessions</dt>
                      <dd className="text-lg sm:text-2xl font-bold text-white">{totalSessions}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-xl mb-8 border border-gray-100">
            <div className="px-6 py-8 sm:p-8">
              <div className="flex items-center mb-6">
                <div className="text-3xl mr-3">🎨</div>
                <h3 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Practice Time by Category</h3>
              </div>
              
              <div className="space-y-6">
                {['Scales', 'Exercises', 'Studies', 'Repertoire', 'Ensemble'].map((category, index) => {
                  const minutes = categoryBreakdown[category] || 0;
                  const detailed = categoryBreakdownDetailed[category] || { guided: 0, free: 0 };
                  const totalMinutes = Object.values(categoryBreakdown).reduce((sum, min) => sum + min, 0);
                  const percentage = totalMinutes > 0 ? (minutes / totalMinutes) * 100 : 0;
                  const guidedPercentage = minutes > 0 ? (detailed.guided / minutes) * 100 : 0;
                  const freePercentage = minutes > 0 ? (detailed.free / minutes) * 100 : 0;
                  const hasPractice = minutes > 0;
                  
                  return (
                    <div key={category} className={`group ${!hasPractice ? 'opacity-60' : ''}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <div className={`text-3xl mr-3 group-hover:scale-110 transition-transform duration-200 ${!hasPractice ? 'grayscale' : ''}`}>
                            {getCategoryIcon(category)}
                          </div>
                          <span className={`text-lg font-semibold ${hasPractice ? 'text-gray-800' : 'text-gray-500'}`}>
                            {category}
                            {!hasPractice && <span className="text-sm ml-2 text-gray-400">(No practice yet)</span>}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className={`text-lg sm:text-xl font-bold ${hasPractice ? 'text-gray-900' : 'text-gray-400'}`}>
                            {formatMinutes(minutes)}
                          </span>
                          {hasPractice && (
                            <span className="text-sm text-gray-500 ml-2">({percentage.toFixed(1)}%)</span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {/* Guided Practice Bar */}
                        <div className="relative">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-600">Guided Practice</span>
                            <span className="text-sm text-gray-500">{formatMinutes(detailed.guided)}</span>
                          </div>
                          <div className="bg-gray-200 rounded-full h-4 shadow-inner">
                            {detailed.guided > 0 && (
                              <div
                                className={`${getCategoryColor(category)} h-4 rounded-full transition-all duration-1000 ease-out shadow-md group-hover:shadow-lg ${!hasPractice ? 'opacity-30' : ''}`}
                                style={{ 
                                  width: `${Math.max((detailed.guided / totalMinutes) * 100, 2)}%`,
                                  animationDelay: `${index * 200}ms`
                                }}
                              ></div>
                            )}
                          </div>
                        </div>
                        
                        {/* Free Practice Bar */}
                        <div className="relative">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-600">Free Practice</span>
                            <span className="text-sm text-gray-500">{formatMinutes(detailed.free)}</span>
                          </div>
                          <div className="bg-gray-200 rounded-full h-4 shadow-inner">
                            {detailed.free > 0 && (
                              <div
                                className={`${getCategoryColor(category)} h-4 rounded-full transition-all duration-1000 ease-out shadow-md group-hover:shadow-lg ${!hasPractice ? 'opacity-30' : ''}`}
                                style={{ 
                                  width: `${Math.max((detailed.free / totalMinutes) * 100, 2)}%`,
                                  animationDelay: `${index * 200 + 100}ms`,
                                  opacity: 0.8
                                }}
                              ></div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="px-6 py-8 sm:p-8">
                <div className="flex items-center mb-6">
                  <div className="text-3xl mr-3">📈</div>
                  <h3 className="text-lg sm:text-2xl font-bold text-white">Session Statistics</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-indigo-100 font-medium">Average Session Length</span>
                    <span className="text-lg sm:text-xl font-bold text-white">{formatMinutes(averageSessionLength)}</span>
                  </div>
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-indigo-100 font-medium">Total Practice Time</span>
                    <span className="text-lg sm:text-xl font-bold text-white">{formatTime(yearlyPracticeTime)}</span>
                  </div>
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-indigo-100 font-medium">Daily Goal</span>
                    <span className="text-lg sm:text-xl font-bold text-white">{currentUser?.practiceSessionDuration || 30} minutes</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-pink-500 to-rose-600 shadow-xl rounded-xl transform hover:scale-105 transition-all duration-300">
              <div className="px-6 py-8 sm:p-8">
                <div className="flex items-center mb-6">
                  <div className="text-3xl mr-3">💡</div>
                  <h3 className="text-lg sm:text-2xl font-bold text-white">Progress Insights</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-pink-100 font-medium">Goal Success Rate</span>
                    <span className="text-lg sm:text-xl font-bold text-white">
                      {totalSessions > 0 ? ((dailyGoalReached / totalSessions) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-pink-100 font-medium">Most Practiced Category</span>
                    <span className="text-lg sm:text-xl font-bold text-white">
                      {Object.keys(categoryBreakdown).length > 0 
                        ? Object.entries(categoryBreakdown).reduce((a, b) => categoryBreakdown[a[0]] > categoryBreakdown[b[0]] ? a : b)[0]
                        : 'N/A'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between items-center bg-white/20 rounded-lg p-3 backdrop-blur-sm">
                    <span className="text-pink-100 font-medium">Weekly Target</span>
                    <span className="text-lg sm:text-xl font-bold text-white">
                      {currentUser?.practiceSessionsPerWeek || 5} sessions
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Analytics;
